import React from 'react'
import styled from '@emotion/styled'
import { iconSet } from 'view/components/icons'
import { openFinPromisify } from 'utils'
import { MultiButton, ButtonStrip } from 'view/components/buttons'

interface TitleBarButtonsProps {
  window: fin.OpenFinWindow
  onClose?: () => unknown
}

type WindowState = 'minimized' | 'maximized' | 'normal'

interface TitleBarButtonsState {
  windowState: WindowState
  minimizable?: boolean
  maximizable?: boolean
}

const ControlsContainer = styled(ButtonStrip)`
  height: 30px;
  border-left: 2px solid #393939;
  padding-left: 10px;
  -webkit-app-region: no-drag;
`

const WindowButton = styled(MultiButton)`
  cursor: pointer;
  svg {
    color: white;
    width: 12px;
    height: 12px;
  }
`

const MinimizeButton = styled(WindowButton)``
const MaximizeButton = styled(WindowButton)``
const RestoreButton = styled(WindowButton)``
const CloseButton = styled(WindowButton)``

export class TitleBarButtons extends React.Component<TitleBarButtonsProps, TitleBarButtonsState> {
  state: TitleBarButtonsState = {
    windowState: 'normal'
  }

  async componentDidMount() {
    const { window } = this.props

    window.addEventListener('maximized', this.handleStateChanged)
    window.addEventListener('minimized', this.handleStateChanged)
    window.addEventListener('restored', this.handleStateChanged)
    window.addEventListener('initialized', this.handleStateChanged)

    const getOptionsAsync = openFinPromisify(window.getOptions, window)
    const options = await getOptionsAsync()
    this.setState({ minimizable: options.minimizable, maximizable: options.maximizable })
  }

  componentWillUnmount() {
    const { window } = this.props
    window.removeEventListener('maximized', this.handleStateChanged)
    window.removeEventListener('minimized', this.handleStateChanged)
    window.removeEventListener('restored', this.handleStateChanged)
    window.removeEventListener('initialized', this.handleStateChanged)
  }

  private handleStateChanged = async () => {
    const { window } = this.props
    const getStateAsync = openFinPromisify(window.getState, window)
    const windowState = await getStateAsync()
    this.setState({ windowState })
  }

  render() {
    const { windowState, minimizable, maximizable } = this.state
    return (
      <ControlsContainer spacing={'10px'}>
        {minimizable && (
          <MinimizeButton onClick={this.handleMinimizeClick} canFocus={false} dataTestId={'TitleBarButtons-MinimizeButton'}>
            {iconSet.minimiseIcon}
          </MinimizeButton>
        )}
        {maximizable && windowState === 'normal' && (
          <MaximizeButton onClick={this.handleMaximizeClick} canFocus={false} dataTestId={'TitleBarButtons-MaximizeButton'}>
            {iconSet.maximiseIcon}
          </MaximizeButton>
        )}
        {maximizable && windowState === 'maximized' && (
          <RestoreButton onClick={this.handleRestoreClick} canFocus={false} dataTestId={'TitleBarButtons-RestoreButton'}>
            {iconSet.restoreIcon}
          </RestoreButton>
        )}
        <CloseButton onClick={this.handleCloseClick} canFocus={false} dataTestId={'TitleBarButtons-CloseButton'}>
          {iconSet.crossIcon}
        </CloseButton>
      </ControlsContainer>
    )
  }

  private handleMinimizeClick = async () => {
    const { window } = this.props
    const minimizeAsync = openFinPromisify(window.minimize, window)
    await minimizeAsync()
  }

  private handleRestoreClick = async () => {
    const { window } = this.props
    const restoreAsync = openFinPromisify(window.restore, window)
    await restoreAsync()
  }

  private handleMaximizeClick = async () => {
    const { window } = this.props
    const maximizeAsync = openFinPromisify(window.maximize, window)
    await maximizeAsync()
  }

  private handleCloseClick = async () => {
    const { window, onClose } = this.props
    if (onClose) {
      onClose()
    }
    const closeAsync = openFinPromisify(window.close, window)
    await closeAsync(false)
  }
}
